import { Avatar, Box, Grid, Typography, useTheme } from '@mui/material'
import { useUpdateUser } from 'api/aws/users/useUpdateUserItems'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useActiveUser } from 'api/slack/useActiveUser'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LegacySectionConfig, ProfileCardAdapter, UniversalReadOnlyField } from 'theme/components'
import { User } from 'types'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import DetailsLoading from '../Loading/DetailsLoading'
import CancelButton from '../ui/cancel-button'
import EditIconButton from '../ui/edit-icon-buttons'
import { SaveChangesButton } from '../ui/save-changes-button'

type ChangedFieldMap = Record<string, { newValue: string; isDirty: boolean }>

const prettifyKey = (key: string) => {
  const keyMap: Record<string, string> = {
    phone: 'Phone',
    linkedIn: 'LinkedIn',
    meta: 'Meta',
    google: 'Google',
    title: 'Title',
    role: 'Role',
  }
  return keyMap[key] || key
}

/**
 * Build field sections configuration for the user profile
 */
const buildUserFieldSections = (): LegacySectionConfig[] => {
  return [
    {
      sectionLabel: 'Contact Information',
      fields: [
        {
          name: 'phone',
          label: 'Phone Number',
          type: 'text',
        },
        {
          name: 'email',
          label: 'Email',
          type: 'text',
          disabled: true, // Email comes from Slack, not editable
        },
        {
          name: 'heartbeat_user_id',
          label: 'Heartbeat User ID',
          type: 'text',
          disabled: true, // Comes from Heartbeat, not editable
        },
      ],
    },
    {
      sectionLabel: 'Social Profiles',
      fields: [
        {
          name: 'linkedIn',
          label: 'LinkedIn URL',
          type: 'text',
        },
        {
          name: 'meta',
          label: 'Meta/Facebook URL',
          type: 'text',
        },
        {
          name: 'google',
          label: 'Google Profile URL',
          type: 'text',
        },
      ],
    },
    {
      sectionLabel: 'Work Details',
      fields: [
        {
          name: 'title',
          label: 'Job Title',
          type: 'text',
          disabled: true, // Comes from Slack
        },
        {
          name: 'role',
          label: 'Role',
          type: 'text',
          disabled: true, // System-managed
        },
      ],
    },
  ]
}

const TeamProfile: React.FC = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { userId } = useParams<{ userId?: string }>()
  const { showSnackbar } = useSnackbarContext()

  // Fetch current logged-in user
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const currentUser = activeUser?.user as User | undefined

  // Fetch all users to find the target user if userId is provided
  const { data: users, isLoading: isUsersLoading } = useUserItems()

  // Determine which user to display
  const user = useMemo(() => {
    if (userId && users) {
      // Find the user by userId param
      return users.find(u => u.user_uuid === userId || u.id === userId)
    }
    // Default to current user
    return currentUser
  }, [userId, users, currentUser])

  // Check if viewing own profile (can edit) or another user's profile (read-only)
  const isOwnProfile = !userId || user?.user_uuid === currentUser?.id

  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [changedFields, setChangedFields] = useState<ChangedFieldMap>({})
  const [isSaving, setIsSaving] = useState(false)

  // Mutation
  const { mutate: updateUser } = useUpdateUser()

  const getFieldValue = (fieldName: string): string => {
    const changed = changedFields[fieldName]?.newValue
    if (changed !== undefined) return changed
    return (user?.[fieldName as keyof User] as string) ?? ''
  }

  const handleChange = (fieldName: string, newValue: string) => {
    const originalValue = (user?.[fieldName as keyof User] as string) ?? ''
    const isDirty = newValue !== originalValue

    setChangedFields(prev => ({
      ...prev,
      [fieldName]: { newValue, isDirty },
    }))
  }

  const handleUpdate = async () => {
    if (!user?.user_uuid || !isOwnProfile) return

    const dirtyEntries = Object.entries(changedFields).filter(([_, meta]) => meta?.isDirty)

    if (dirtyEntries.length === 0) {
      console.warn('No updates found')
      return
    }

    setIsSaving(true)

    // Build patch object with all dirty fields
    const patch: Record<string, string> = {}
    for (const [fieldName, meta] of dirtyEntries) {
      patch[fieldName] = meta.newValue
    }

    updateUser(
      {
        user_uuid: user.user_uuid,
        patch,
      },
      {
        onSuccess: () => {
          showSnackbar('Profile updated successfully', 'success')
          setIsSaving(false)
          setChangedFields({})
          setIsEditing(false)
        },
        onError: err => {
          console.error('Update failed:', err)
          showSnackbar('Failed to update profile', 'error')
          setIsSaving(false)
        },
      }
    )
  }

  const handleCancel = () => {
    setChangedFields({})
    setIsEditing(false)
  }

  const hasDirtyFields = Object.values(changedFields).some(v => v?.isDirty)
  const isLoading = isActiveUserLoading || (userId && isUsersLoading)

  // Build sections config
  const userFieldSections = useMemo(() => {
    return buildUserFieldSections()
  }, [])

  if (isLoading) return <DetailsLoading />

  if (!user) {
    return (
      <Box p={3}>
        <Typography>Unable to load user profile</Typography>
      </Box>
    )
  }

  const userName =
    user.name || `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim()
  const userPhoto = user.image_512 || (user as any).image_192 || (user as any).image_72

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{
        pt: pinned ? '60px' : 0,
        position: 'relative',
        transition: 'padding 0.3s ease',
      }}
    >
      {/* Scrollable content area */}
      <Box
        flex={1}
        overflow="auto"
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow:
              theme.palette.mode === 'dark' ? 'inset 0 0 6px rgba(0,0,0,0.3)' : 'inset 0 0 6px rgba(255,255,255,0.3)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : 'rgba(0,0,0,0.5)',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'text.secondary',
            },
          },
        }}
      >
        {/* User Header Card */}
        <Box
          sx={{
            px: 3,
            py: 2,
            mx: 3,
            mt: 2,
            mb: 2,
            borderRadius: '20px',
            bgcolor: theme.palette.background.section,
          }}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar
              src={userPhoto}
              alt={userName}
              sx={{
                width: 100,
                height: 100,
                border: `3px solid ${theme.palette.primary.main}`,
              }}
            />
            <Box flex={1}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                {userName}
                {!isOwnProfile && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                    (Viewing profile)
                  </Typography>
                )}
              </Typography>
              {user.title && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 0.25 }}>
                  {user.title}
                </Typography>
              )}
              {user.role && (
                <Typography variant="body2" color="text.secondary">
                  {user.role}
                </Typography>
              )}
              {user.email && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Profile Fields using ProfileCardAdapter */}
        <Box sx={{ px: 3, pb: 3 }}>
          <ProfileCardAdapter
            sections={userFieldSections}
            isEditing={isEditing && isOwnProfile}
            entity={user}
            getFieldValue={getFieldValue}
            onChange={handleChange}
            changedFields={changedFields}
            savingFields={isSaving ? Object.keys(changedFields).filter(k => changedFields[k]?.isDirty) : []}
            ReadOnlyFieldComponent={UniversalReadOnlyField}
            renderContext={{ users }}
            enhanced={true}
          />
        </Box>
      </Box>

      {/* Sticky footer for edit/save controls - only show for own profile */}
      {isOwnProfile && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: theme.palette.background.paper,
            px: 3,
            py: 1.5,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.3)',
            zIndex: 10,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
            {isEditing ? (
              <>
                <Grid item>
                  <Typography color={theme.palette.text.primary} variant="subtitle1">
                    Changed Fields:
                  </Typography>
                </Grid>

                <Grid item sx={{ maxWidth: '60%', flexGrow: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {Object.entries(changedFields)
                      .filter(([_, meta]) => meta.isDirty)
                      .map(([key]) => (
                        <Box
                          component="span"
                          key={key}
                          sx={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            border: `1px solid ${theme.palette.text.primary}`,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                            background: 'transparent',
                          }}
                        >
                          {prettifyKey(key)}
                        </Box>
                      ))}
                  </Box>
                </Grid>
                <Grid item>
                  <CancelButton onClick={handleCancel} disabled={isSaving} />
                </Grid>
                <Grid item>
                  <SaveChangesButton
                    onClick={handleUpdate}
                    disabled={!hasDirtyFields || isSaving}
                    variant="contained"
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </SaveChangesButton>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <EditIconButton onEdit={() => setIsEditing(true)} />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  )
}

export default TeamProfile
